
import axios from "./config";

/**
 * create new Project
 *
 * @function
 * @name createProject
 *
 * @version 0.1.0
 * @since 0.1.0
 */
const createProjects = (project) =>
    axios
        .post(`/projects`, project)
        .then((response) => response.data);




/**
 * Fetch all projects from API
 *
 */
const getProjects = (filter = {}, page = 1, perPage = 10) => {
    const params = {
        ...filter,
        page,
        per_page: perPage
    };
    return axios.get(`/projects`, { params }).then((response) => response.data);
}


/**
 * edit existing project
 *
 * @function
 * @name updateProject
 *
 * @version 0.1.0
 * @since 0.1.0
 */
const updateProject = (project, id) => {
    return axios
        .patch(`/projects/${id}`, project)
        .then((response) => response.data);
};

/**
 * detaches a project from list
 *
 * @function
 * @name deleteProject
 *
 * @version 0.1.0
 * @since 0.1.0
 */
const deleteProject = (project) => {
    return axios
        .delete(`projects/${project}`)
        .then((response) => response.data);
};

/**
 * Get a projects from the API
 * @function
 * @name getProject
 *
 * @param {Object} id - Id of an Human resource
 * @returns {Promise}
 *
 * @version 0.1.0
 * @since 0.1.0
 */
const ensureValidId = (id, name = 'id') => {
    if (id === undefined || id === null || id === '' || `${id}`.toLowerCase() === 'null' || `${id}`.toLowerCase() === 'undefined') {
        return false;
    }
    return true;
};

const getProject = (id) => {
    if (!ensureValidId(id, 'projectId')) {
        return Promise.reject(new Error('Invalid project id'));
    }
    return axios.get(`/projects/${id}`).then((response) => response.data);
}

/**
 * Get regions for a project
 * @function
 * @name getProjectRegions
 * @param {number|string} id - Project ID
 * @param {number} page - Page number
 * @param {number} perPage - Items per page
 * @returns {Promise}
 */
const getProjectRegions = (id, page = 1, perPage = 10, extraParams = {}) => {
    if (!ensureValidId(id, 'projectId')) {
        return Promise.reject(new Error('Invalid project id'));
    }
    const params = { page, per_page: perPage, ...extraParams };
    return axios.get(`/projects/${id}/regions`, { params }).then((response) => response.data);
}

/**
 * Get councils (LGAs) for a project
 * @function
 * @name getProjectCouncils
 * @param {number|string} id - Project ID
 * @param {number} page - Page number
 * @param {number} perPage - Items per page
 * @returns {Promise}
 */
const getProjectCouncils = (id, page = 1, perPage = 10) => {
    if (!ensureValidId(id, 'projectId')) {
        return Promise.reject(new Error('Invalid project id'));
    }
    const params = { page, per_page: perPage };
    return axios.get(`/projects/${id}/councils`, { params }).then((response) => response.data);
}

/**
 * Get a single region for a project
 * @function
 * @name getProjectRegion
 * @param {number|string} projectId - Project ID
 * @param {number|string} regionId - Region ID
 * @returns {Promise}
 */
const getProjectRegion = (projectId, regionId) => {
    if (!ensureValidId(projectId, 'projectId') || !ensureValidId(regionId, 'regionId')) {
        return Promise.reject(new Error('Invalid project or region id'));
    }
    return axios.get(`/projects/${projectId}/regions/${regionId}`).then((response) => response.data);
}

/**
 * Get councils (LGAs) within a specific region for a project
 * @function
 * @name getProjectRegionCouncils
 * @param {number|string} projectId - Project ID
 * @param {number|string} regionId - Region ID
 * @returns {Promise}
 */
const getProjectRegionCouncils = (projectId, regionId) => {
    if (!ensureValidId(projectId, 'projectId') || !ensureValidId(regionId, 'regionId')) {
        return Promise.reject(new Error('Invalid project or region id'));
    }
    return axios.get(`/projects/${projectId}/regions/${regionId}/councils`).then((response) => response.data);
}

/**
 * Get a single council (LGA) for a project
 * @function
 * @name getProjectCouncil
 * @param {number|string} projectId - Project ID
 * @param {number|string} councilId - Council ID
 * @returns {Promise}
 */
const getProjectCouncil = (projectId, councilId) => {
    if (!ensureValidId(projectId, 'projectId') || !ensureValidId(councilId, 'councilId')) {
        return Promise.reject(new Error('Invalid project or council id'));
    }
    return axios.get(`/projects/${projectId}/councils/${councilId}`).then((response) => response.data);
}

/**
 * @function
 * @name getProjectsStatistics
 * @description returns statistic of a project when project id is provided
 * otherwise returns statistics  of specified project
 * @param {Object} id - Id of project
 * @returns {Promise}
 *
 * @version 0.1.0
 * @since 0.1.0
 */
const getProjectsStatistics = (id = '') => {
    return axios.get(`/projects/statistics${id}`, { params: { id } }).then((response) => response.data);
}

/**
 * Fetch all projects from API
 *
 */
const getCurrencies = () => {
    return axios.get(`/currencies`).then((response) => response.data);
}


/**
 * @function
 * @name postTotalCost
 * */
const postTotalCost = (total_cost) =>
    axios.post(`/money`, total_cost).then((response) => response.data);

/**
 * @function
 * @name createProjectDetails
 * */
const createProjectDetails = (details) =>
    axios.post(`/project_details`, details).then((response) => response.data);

/**
 * Fetch all projects status from API
 *
 */
const getProjectStatus = () => {
    return axios.get(`/project_status`).then((response) => response.data);
}

/**
 * @function
 * @name createProjectComponents
 * */
const createProjectComponents = (details) =>
    axios.post(`/project_components`, details).then((response) => response.data);

/**
 * @function
 * @name getProjectComponents
 * @description Fetch all project components from API
 * @param {Object} filter - Filter parameters
 * @param {number} page - Page number
 * @param {number} perPage - Items per page
 * */
const getProjectComponents = (filter = {}, page = 1, perPage = 10) => {
    const params = {
        ...filter,
        page,
        per_page: perPage
    };
    return axios.get(`/project_components`, { params }).then((response) => response.data);
};

/**
 * @function
 * @name createProjectSubComponent
 * */
const createProjectSubComponent = (details) =>
    axios.post(`/project_sub_components`, details).then((response) => response.data);


/**
 * Fetch all projects sub-componennt from API
 * @param {Object} filter - Filter parameters
 * @param {number} page - Page number
 * @param {number} perPage - Items per page
 */
const getProjectSubComponent = (filter = {}, page = 1, perPage = 10) => {
    const params = {
        ...filter,
        page,
        per_page: perPage
    };
    return axios.get(`/project_sub_components`, { params }).then((response) => response.data);
}

/**
 * Get financiers for a project
 * @function
 * @name getProjectFinanciers
 * @param {number|string} projectId - Project ID
 * @param {number} page - Page number
 * @param {number} perPage - Items per page
 * @returns {Promise}
 */
const getProjectFinanciers = (projectId, page = 1, perPage = 10) => {
    if (!ensureValidId(projectId, 'projectId')) {
        return Promise.reject(new Error('Invalid project id'));
    }
    const params = { page, per_page: perPage };
    return axios.get(`/projects/${projectId}/financiers`, { params }).then((response) => response.data);
}

/**
 * @function
 * @name openProjectTicket
 * */
const openProjectTicket =(details) => 
axios.post(`/projects/create_ticket`, details).then((response) => response.data);


export default {
    createProjects,
    getProjects,
    updateProject,
    deleteProject,
    getProject,
    getProjectRegions,
    getProjectCouncils,
    getProjectRegion,
    getProjectRegionCouncils,
    getProjectCouncil,
    getProjectsStatistics,
    getCurrencies,
    postTotalCost,
    createProjectDetails,
    getProjectStatus,
    createProjectComponents,
    getProjectComponents,
    openProjectTicket,
    createProjectSubComponent,
    getProjectSubComponent,
    getProjectFinanciers
}
